import React from 'react';
import SEO from '../components/SEO.jsx';

const Privacy = () => {
  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO
        title="Privacy Policy"
        description="Privacy policy for Bioresonance Leicester. How we collect, use and protect your personal information when you use our website or services."
        path="/privacy"
      />
      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            TP Wellness
          </p>
          <p className="text-gray-600 mt-2">
            Last updated: 1 February 2026
          </p>
        </header>

        <article className="space-y-10 text-dark-grey">
          <section>
            <p className="mb-4">
              This Privacy Policy describes how TP Wellness (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, and shares your personal information when you visit or use the website{' '}
              <a href="https://tpwellness.co.uk" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-medium">https://tpwellness.co.uk</a>
              {' '}(the &ldquo;Site&rdquo;) or when you contact or purchase services from us.
            </p>
          </section>

          {/* 1. Personal Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>1. Personal Information We Collect</h2>

            <h3 className="text-xl font-semibold text-dark-grey mb-2">a) Information collected automatically</h3>
            <p className="mb-4">
              When you visit the Site, we automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>web browser type</li>
              <li>IP address</li>
              <li>time zone</li>
              <li>cookie information</li>
              <li>pages viewed, referring websites, and how you interact with the Site</li>
            </ul>
            <p className="mb-4">
              This information is referred to as Device Information.
            </p>
            <p className="mb-2">We collect Device Information using:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Cookies</strong> – small data files placed on your device.
                You can learn more about cookies and how to disable them at:{' '}
                <a href="http://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">http://www.allaboutcookies.org</a>
              </li>
              <li>
                <strong>Log files</strong> – which track actions on the Site and collect IP address, browser type, ISP, referring/exit pages, and date/time stamps
              </li>
              <li>
                <strong>Web beacons, tags, and pixels</strong> – used to understand browsing behaviour
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-dark-grey mb-2 mt-6">b) Information you provide to us</h3>
            <p className="mb-4">
              When you make a booking, enquiry, or purchase a service, we collect certain information, including:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>name</li>
              <li>email address</li>
              <li>phone number</li>
              <li>billing details</li>
              <li>appointment and service details</li>
            </ul>
            <p className="mb-4">
              This information is referred to as Order Information.
            </p>

            <h3 className="text-xl font-semibold text-dark-grey mb-2">c) Personal Information</h3>
            <p>
              When we refer to Personal Information in this Privacy Policy, we mean both Device Information and Order Information.
            </p>
          </section>

          {/* 2. How We Use Your Personal Information */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>2. How We Use Your Personal Information</h2>
            <p className="mb-4">
              We use your Personal Information for the following purposes:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>to process bookings and payments</li>
              <li>to provide services and appointment confirmations</li>
              <li>to communicate with you</li>
              <li>to issue invoices or receipts</li>
              <li>to screen for potential fraud or misuse</li>
              <li>to improve and optimise our Site</li>
              <li>to comply with legal and regulatory obligations</li>
            </ul>
            <p>
              Where you have given consent, we may also use your information to provide marketing communications about our services.
            </p>
          </section>

          {/* 3. Legal Basis for Processing (UK GDPR) */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>3. Legal Basis for Processing (UK GDPR)</h2>
            <p className="mb-4">
              We process your personal data under the following legal bases:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Performance of a contract</strong> – to provide services you have booked</li>
              <li><strong>Legal obligation</strong> – for accounting and regulatory purposes</li>
              <li><strong>Legitimate interests</strong> – improving our services and website</li>
              <li><strong>Consent</strong> – for marketing communications (you may withdraw consent at any time)</li>
            </ul>
          </section>

          {/* 4. Sharing Your Personal Information */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>4. Sharing Your Personal Information</h2>
            <p className="mb-4">
              We share your Personal Information with third parties only where necessary, including:
            </p>
            <p className="mb-4">
              Website analytics providers, such as Google Analytics, to understand how visitors use our Site
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>
                Google&rsquo;s privacy policy:{' '}
                <a href="https://www.google.com/policies/privacy/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://www.google.com/policies/privacy/</a>
              </li>
              <li>
                Google Analytics opt-out:{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://tools.google.com/dlpage/gaoptout</a>
              </li>
            </ul>
            <p>
              We may also disclose Personal Information if required to do so by law or in response to lawful requests from public authorities.
            </p>
          </section>

          {/* 5. International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>5. International Transfers</h2>
            <p>
              Some service providers we use (such as analytics providers) may process data outside the UK or EEA, including in the United States or Canada.
              Where this occurs, we ensure appropriate safeguards are in place in accordance with UK GDPR requirements.
            </p>
          </section>

          {/* 6. Behavioural Advertising */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>6. Behavioural Advertising</h2>
            <p className="mb-4">
              We may use your Personal Information to provide marketing communications that may be of interest to you.
            </p>
            <p className="mb-2">You can opt out of targeted advertising via:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>
                Facebook: <a href="https://www.facebook.com/settings/?tab=ads" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://www.facebook.com/settings/?tab=ads</a>
              </li>
              <li>
                Google: <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://www.google.com/settings/ads</a>
              </li>
              <li>
                Bing: <a href="https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads</a>
              </li>
            </ul>
            <p>
              You can also opt out via the Digital Advertising Alliance:{' '}
              <a href="http://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">http://optout.aboutads.info/</a>
            </p>
          </section>

          {/* 7. Do Not Track */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>7. Do Not Track</h2>
            <p>
              Please note that we do not alter our Site&rsquo;s data collection practices when we receive a &ldquo;Do Not Track&rdquo; signal from your browser.
            </p>
          </section>

          {/* 8. Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>8. Your Rights</h2>
            <p className="mb-4">
              Under UK GDPR, you have the right to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>access your personal data</li>
              <li>request correction of inaccurate data</li>
              <li>request deletion of your data</li>
              <li>object to or restrict processing</li>
              <li>withdraw consent at any time</li>
              <li>lodge a complaint with the Information Commissioner&rsquo;s Office (ICO)</li>
            </ul>
            <p>
              To exercise your rights, please contact us using the details below.
            </p>
          </section>

          {/* 9. Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>9. Data Retention</h2>
            <p>
              We retain your Personal Information only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
            </p>
          </section>

          {/* 10. Changes to This Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.
              The most current version will always be available on our website.
            </p>
          </section>

          {/* 11. Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gold mb-4" style={{ fontFamily: "Playfair Display, serif" }}>11. Contact Us</h2>
            <p className="mb-4">
              For more information about our privacy practices, or to make a data request or complaint, please contact us:
            </p>
            <p className="mb-1">
              <strong>Email:</strong>{' '}
              <a href="mailto:info@bioresonance-leicester.co.uk" className="text-gold hover:underline">info@bioresonance-leicester.co.uk</a>
            </p>
            <p className="mb-1"><strong>Address:</strong></p>
            <p className="pl-4">
              TP Wellness<br />
              108 New Walk<br />
              3rd Floor, Room 303<br />
              Leicester, LE1 7EA<br />
              United Kingdom
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default Privacy;
